// src/pages/TemplatesAdmin.js
// Admin page for managing WhatsApp templates in Firestore

import React, { useState } from 'react';
import { ContentLayout, PageContainer } from '../components/layout/Layout';
import { Card, CardContent, Badge, Button, Modal, ModalFooter, Input, TextArea } from '../components/common';
import { useBusinessConfig } from '../context/BusinessConfigContext';
import { useTemplates } from '../hooks/useTemplates';
import TemplateAPI from '../services/api/templateAPI';
import {
  Shield, RefreshCw, Database, MessageSquare, Zap, Clock,
  ChevronDown, ChevronUp, Check, X, Plus, Edit2, Trash2,
  ToggleLeft, ToggleRight
} from 'lucide-react';

// Trigger type options
const TRIGGER_TYPE_OPTIONS = [
  { value: 'before_appointment', label: 'לפני תור' },
  { value: 'after_appointment', label: 'אחרי תור' },
  { value: 'appointment_created', label: 'לקוח מזמין תור' },
  { value: 'appointment_approved', label: 'תור נקבע' },
  { value: 'campaign', label: 'קמפיין' }
];

// Variable source options
const VARIABLE_SOURCE_OPTIONS = [
  { value: 'appointment.customer_name', label: 'שם לקוח' },
  { value: 'appointment.date', label: 'תאריך תור' },
  { value: 'appointment.time', label: 'שעת תור' },
  { value: 'appointment.service_name', label: 'שם שירות' },
  { value: 'appointment.crew_member_name', label: 'שם איש צוות' },
  { value: 'business.business_name', label: 'שם העסק' },
  { value: 'business.business_phone', label: 'טלפון העסק' },
  { value: 'business.business_location', label: 'כתובת העסק' },
  { value: 'business.business_slug', label: 'מזהה עסק (slug)' },
  { value: 'system.booking_link', label: 'קישור הזמנה' },
  { value: 'user.custom', label: 'ערך מותאם אישית' }
];

// Empty template form data
const EMPTY_TEMPLATE = {
  id: '',
  name: '',
  description: '',
  triggerTypes: [],
  defaultTiming: 24,
  isCampaignTemplate: false,
  active: true,
  whatsapp: {
    contentSid: '',
    messagingServiceSid: ''
  },
  variables: [],
  content: ''
};

export function TemplatesAdmin({ onNavigate }) {
  const { isAdmin } = useBusinessConfig();
  const { templates, loading, error, refresh, hasLoaded } = useTemplates();

  // State
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [formData, setFormData] = useState(EMPTY_TEMPLATE);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [actionResult, setActionResult] = useState(null);

  // Open modal for adding new template
  const handleAddTemplate = () => {
    setFormData(EMPTY_TEMPLATE);
    setFormErrors({});
    setModalMode('add');
    setIsModalOpen(true);
  };

  // Open modal for editing existing template
  const handleEditTemplate = (template) => {
    setFormData({
      ...EMPTY_TEMPLATE,
      ...template,
      whatsapp: template.whatsapp || { contentSid: '', messagingServiceSid: '' },
      variables: template.variables || [],
      triggerTypes: template.triggerTypes || []
    });
    setFormErrors({});
    setModalMode('edit');
    setIsModalOpen(true);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith('whatsapp.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        whatsapp: { ...prev.whatsapp, [field]: value }
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Handle trigger type multi-select
  const handleTriggerTypeToggle = (triggerType) => {
    setFormData(prev => {
      const current = prev.triggerTypes || [];
      if (current.includes(triggerType)) {
        return { ...prev, triggerTypes: current.filter(t => t !== triggerType) };
      } else {
        return { ...prev, triggerTypes: [...current, triggerType] };
      }
    });
  };

  // Add a new variable
  const handleAddVariable = () => {
    setFormData(prev => ({
      ...prev,
      variables: [
        ...prev.variables,
        { name: '', displayName: '', source: '', order: prev.variables.length + 1 }
      ]
    }));
  };

  // Update variable
  const handleVariableChange = (index, field, value) => {
    setFormData(prev => {
      const newVariables = [...prev.variables];
      newVariables[index] = { ...newVariables[index], [field]: value };
      return { ...prev, variables: newVariables };
    });
  };

  // Remove variable
  const handleRemoveVariable = (index) => {
    setFormData(prev => ({
      ...prev,
      variables: prev.variables.filter((_, i) => i !== index).map((v, i) => ({ ...v, order: i + 1 }))
    }));
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    if (!formData.id?.trim()) errors.id = 'מזהה תבנית הוא שדה חובה';
    if (!formData.name?.trim()) errors.name = 'שם תבנית הוא שדה חובה';
    if (!formData.whatsapp?.contentSid?.trim()) errors['whatsapp.contentSid'] = 'Content SID הוא שדה חובה';
    if (!formData.triggerTypes?.length) errors.triggerTypes = 'יש לבחור לפחות סוג טריגר אחד';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Save template
  const handleSaveTemplate = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setActionResult(null);

    try {
      if (modalMode === 'add') {
        await TemplateAPI.create(formData);
        setActionResult({ success: true, message: 'התבנית נוצרה בהצלחה' });
      } else {
        await TemplateAPI.update(formData.id, formData);
        setActionResult({ success: true, message: 'התבנית עודכנה בהצלחה' });
      }
      setIsModalOpen(false);
      await refresh();
    } catch (err) {
      setActionResult({ success: false, message: err.message });
    } finally {
      setSaving(false);
    }
  };

  // Toggle template active status
  const handleToggleActive = async (template) => {
    try {
      await TemplateAPI.toggleActive(template.id);
      setActionResult({
        success: true,
        message: `התבנית ${template.active !== false ? 'הושבתה' : 'הופעלה'} בהצלחה`
      });
      await refresh();
    } catch (err) {
      setActionResult({ success: false, message: err.message });
    }
  };

  // Delete template
  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(`האם אתה בטוח שברצונך למחוק את התבנית "${template.name}"?`)) {
      return;
    }

    try {
      await TemplateAPI.delete(template.id);
      setActionResult({ success: true, message: 'התבנית נמחקה בהצלחה' });
      await refresh();
    } catch (err) {
      setActionResult({ success: false, message: err.message });
    }
  };

  // Helper functions
  const getTriggerTypeLabel = (type) => {
    const option = TRIGGER_TYPE_OPTIONS.find(o => o.value === type);
    return option?.label || type;
  };

  const getTriggerTypeColor = (type) => {
    const colors = {
      'before_appointment': 'bg-yellow-100 text-yellow-800',
      'after_appointment': 'bg-green-100 text-green-800',
      'appointment_created': 'bg-blue-100 text-blue-800',
      'appointment_approved': 'bg-purple-100 text-purple-800',
      'campaign': 'bg-pink-100 text-pink-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  // If not admin, show access denied
  if (!isAdmin) {
    return (
      <ContentLayout
        currentPage="templates-admin"
        onNavigate={onNavigate}
        title="ניהול תבניות"
        description="ניהול תבניות WhatsApp"
      >
        <PageContainer>
          <div className="flex flex-col items-center justify-center py-12">
            <Shield className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">אין הרשאת מנהל</h3>
            <p className="text-gray-600 text-center max-w-md">
              אין לך הרשאה לגשת לעמוד זה. רק משתמשים עם הרשאות מנהל יכולים לנהל תבניות.
            </p>
          </div>
        </PageContainer>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      currentPage="templates-admin"
      onNavigate={onNavigate}
      title="ניהול תבניות WhatsApp"
      description="צפייה וניהול תבניות הודעות אוטומטיות"
      primaryAction={{
        label: 'הוסף תבנית',
        onClick: handleAddTemplate,
        icon: <Plus className="w-4 h-4" />
      }}
      secondaryAction={{
        label: loading ? 'טוען...' : 'רענן',
        onClick: () => refresh(),
        disabled: loading,
        icon: <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
      }}
    >
      <PageContainer>
        {/* Action Result Message */}
        {actionResult && (
          <div className={`mb-6 p-4 rounded-lg ${actionResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {actionResult.success ? (
                  <Check className="w-5 h-5 text-green-600" />
                ) : (
                  <X className="w-5 h-5 text-red-600" />
                )}
                <span className={actionResult.success ? 'text-green-800' : 'text-red-800'}>
                  {actionResult.message}
                </span>
              </div>
              <button onClick={() => setActionResult(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200">
            <p className="text-red-800">שגיאה בטעינת תבניות: {error}</p>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-blue-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">סה"כ תבניות</p>
                  <p className="text-2xl font-bold text-blue-600">{templates.length}</p>
                </div>
                <MessageSquare className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">תבניות פעילות</p>
                  <p className="text-2xl font-bold text-green-600">
                    {templates.filter(t => t.active !== false).length}
                  </p>
                </div>
                <Check className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">תבניות אוטומציה</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {templates.filter(t => !t.isCampaignTemplate).length}
                  </p>
                </div>
                <Zap className="w-8 h-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-pink-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">תבניות קמפיין</p>
                  <p className="text-2xl font-bold text-pink-600">
                    {templates.filter(t => t.isCampaignTemplate).length}
                  </p>
                </div>
                <MessageSquare className="w-8 h-8 text-pink-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Loading State */}
        {loading && !hasLoaded && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
            <span className="mr-3 text-gray-600">טוען תבניות...</span>
          </div>
        )}

        {/* Empty State */}
        {hasLoaded && templates.length === 0 && (
          <div className="text-center py-12">
            <Database className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">אין תבניות</h3>
            <p className="text-gray-600 mb-4">
              לא נמצאו תבניות ב-Firestore. לחץ על "העלה תבניות ראשוניות" או "הוסף תבנית" כדי להתחיל.
            </p>
          </div>
        )}

        {/* Templates List */}
        {hasLoaded && templates.length > 0 && (
          <div className="space-y-4">
            {templates.map((template) => (
              <Card
                key={template.id}
                className={`transition-all duration-200 ${template.active === false ? 'opacity-60' : ''}`}
              >
                <CardContent className="p-0">
                  {/* Header */}
                  <div
                    className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedTemplate(expandedTemplate === template.id ? null : template.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <MessageSquare className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">{template.name}</h3>
                          <p className="text-sm text-gray-500">{template.id}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        {/* Action Buttons */}
                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => handleEditTemplate(template)}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="ערוך"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleActive(template)}
                            className={`p-2 rounded-lg transition-colors ${
                              template.active !== false
                                ? 'text-green-600 hover:bg-green-50'
                                : 'text-gray-400 hover:bg-gray-100'
                            }`}
                            title={template.active !== false ? 'השבת' : 'הפעל'}
                          >
                            {template.active !== false ? (
                              <ToggleRight className="w-4 h-4" />
                            ) : (
                              <ToggleLeft className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleDeleteTemplate(template)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="מחק"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Badges */}
                        <Badge
                          variant={template.active !== false ? 'success' : 'secondary'}
                          className={template.active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}
                        >
                          {template.active !== false ? 'פעיל' : 'לא פעיל'}
                        </Badge>

                        {template.triggerTypes?.slice(0, 2).map((trigger, idx) => (
                          <Badge key={idx} className={getTriggerTypeColor(trigger)}>
                            {getTriggerTypeLabel(trigger)}
                          </Badge>
                        ))}
                        {template.triggerTypes?.length > 2 && (
                          <Badge className="bg-gray-100 text-gray-600">+{template.triggerTypes.length - 2}</Badge>
                        )}

                        {template.isCampaignTemplate && (
                          <Badge className="bg-pink-100 text-pink-800">קמפיין</Badge>
                        )}

                        {expandedTemplate === template.id ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedTemplate === template.id && (
                    <div className="border-t border-gray-100 p-4 bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          {template.description && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-1">תיאור</h4>
                              <p className="text-sm text-gray-600">{template.description}</p>
                            </div>
                          )}

                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">הגדרות WhatsApp</h4>
                            <div className="bg-white rounded-lg p-3 border border-gray-200 text-sm font-mono">
                              <div className="flex justify-between py-1">
                                <span className="text-gray-500">contentSid:</span>
                                <span className="text-gray-900">{template.whatsapp?.contentSid || '—'}</span>
                              </div>
                              <div className="flex justify-between py-1">
                                <span className="text-gray-500">messagingServiceSid:</span>
                                <span className="text-gray-900 text-xs">{template.whatsapp?.messagingServiceSid || '—'}</span>
                              </div>
                            </div>
                          </div>

                          {template.defaultTiming && (
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-600">
                                תזמון ברירת מחדל: {template.defaultTiming} שעות
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="space-y-4">
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">
                              משתנים ({template.variables?.length || 0})
                            </h4>
                            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                              <table className="min-w-full text-sm">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-3 py-2 text-right text-gray-600">#</th>
                                    <th className="px-3 py-2 text-right text-gray-600">שם</th>
                                    <th className="px-3 py-2 text-right text-gray-600">מקור</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {template.variables?.sort((a, b) => a.order - b.order).map((variable) => (
                                    <tr key={variable.name} className="border-t border-gray-100">
                                      <td className="px-3 py-2 text-gray-500">{variable.order}</td>
                                      <td className="px-3 py-2 font-medium">{variable.displayName}</td>
                                      <td className="px-3 py-2 font-mono text-xs text-gray-500">{variable.source}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {template.content && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-2">תוכן ההודעה</h4>
                              <div className="bg-white rounded-lg p-3 border border-gray-200">
                                <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans" dir="rtl">
                                  {template.content}
                                </pre>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </PageContainer>

      {/* Add/Edit Template Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalMode === 'add' ? 'הוסף תבנית חדשה' : 'ערוך תבנית'}
        size="lg"
      >
        <div className="space-y-6 max-h-[70vh] overflow-y-auto p-1">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">מזהה תבנית *</label>
              <Input
                name="id"
                value={formData.id}
                onChange={handleInputChange}
                placeholder="bq_template_name"
                disabled={modalMode === 'edit'}
                className={formErrors.id ? 'border-red-300' : ''}
              />
              {formErrors.id && <p className="mt-1 text-sm text-red-600">{formErrors.id}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">שם תבנית *</label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="תזכורת לתור"
                className={formErrors.name ? 'border-red-300' : ''}
              />
              {formErrors.name && <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">תיאור</label>
            <TextArea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="תיאור התבנית..."
              rows={2}
            />
          </div>

          {/* Trigger Types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">סוגי טריגר *</label>
            <div className="flex flex-wrap gap-2">
              {TRIGGER_TYPE_OPTIONS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleTriggerTypeToggle(option.value)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    formData.triggerTypes?.includes(option.value)
                      ? 'bg-blue-100 text-blue-800 border-2 border-blue-300'
                      : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {formErrors.triggerTypes && <p className="mt-1 text-sm text-red-600">{formErrors.triggerTypes}</p>}
          </div>

          {/* WhatsApp Config */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content SID *</label>
              <Input
                name="whatsapp.contentSid"
                value={formData.whatsapp?.contentSid || ''}
                onChange={handleInputChange}
                placeholder="HX..."
                className={formErrors['whatsapp.contentSid'] ? 'border-red-300' : ''}
              />
              {formErrors['whatsapp.contentSid'] && <p className="mt-1 text-sm text-red-600">{formErrors['whatsapp.contentSid']}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Messaging Service SID</label>
              <Input
                name="whatsapp.messagingServiceSid"
                value={formData.whatsapp?.messagingServiceSid || ''}
                onChange={handleInputChange}
                placeholder="MG..."
              />
            </div>
          </div>

          {/* Timing & Flags */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">תזמון ברירת מחדל (שעות)</label>
              <Input
                type="number"
                name="defaultTiming"
                value={formData.defaultTiming}
                onChange={handleInputChange}
                min={0}
              />
            </div>

            <div className="flex items-center gap-4 pt-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="isCampaignTemplate"
                  checked={formData.isCampaignTemplate}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">תבנית קמפיין</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">פעיל</span>
              </label>
            </div>
          </div>

          {/* Variables */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">משתנים</label>
              <button
                type="button"
                onClick={handleAddVariable}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                הוסף משתנה
              </button>
            </div>

            {formData.variables?.length > 0 && (
              <div className="space-y-2">
                {formData.variables.map((variable, index) => (
                  <div key={index} className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg">
                    <span className="text-sm text-gray-500 w-6">{index + 1}</span>
                    <Input
                      value={variable.name}
                      onChange={(e) => handleVariableChange(index, 'name', e.target.value)}
                      placeholder="name"
                      className="flex-1"
                    />
                    <Input
                      value={variable.displayName}
                      onChange={(e) => handleVariableChange(index, 'displayName', e.target.value)}
                      placeholder="שם תצוגה"
                      className="flex-1"
                    />
                    <select
                      value={variable.source}
                      onChange={(e) => handleVariableChange(index, 'source', e.target.value)}
                      className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                    >
                      <option value="">בחר מקור...</option>
                      {VARIABLE_SOURCE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => handleRemoveVariable(index)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">תוכן ההודעה</label>
            <TextArea
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              placeholder="תוכן ההודעה עם משתנים כמו {{1}}, {{2}}..."
              rows={4}
              dir="rtl"
            />
          </div>
        </div>

        <ModalFooter>
          <Button variant="secondary" onClick={() => setIsModalOpen(false)} disabled={saving}>
            ביטול
          </Button>
          <Button
            onClick={handleSaveTemplate}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {saving ? 'שומר...' : modalMode === 'add' ? 'צור תבנית' : 'שמור שינויים'}
          </Button>
        </ModalFooter>
      </Modal>
    </ContentLayout>
  );
}
